import { type ReactNode } from "react"
import { Container } from "@/screens/rewards/components/Container"
import { Horizontal, Vertical } from "@/components/standard/BFLayout"
import { H4, H6, Small, Tiny } from "@/components/standard/BFText"
import { BFButton } from "@/components/buttons/CommonButtons"
import { router } from "expo-router"
import { preQualLandingPageIntentClick } from "@/analytics/events/preQual"
import type { Icon as PhosphorIcon } from "phosphor-react-native/src/index"
import { BFColors } from "@/constants/Colors"

import StarIcon from "phosphor-react-native/src/icons/Star"
import MoneyWavyIcon from "phosphor-react-native/src/icons/MoneyWavy"
import RocketLaunchIcon from "phosphor-react-native/src/icons/RocketLaunch"
import KeyIcon from "phosphor-react-native/src/icons/Key"
import MountainsIcon from "phosphor-react-native/src/icons/Mountains"

interface LoanProductCardProps {
  name: string
  icon: ReactNode
  description: string
  className?: string

  ltc: string
  interest: string
  origination: string
  noCta?: boolean
}

export const LoanProductIcon = ({
  Icon,
}: {
  Icon: React.ComponentType<React.ComponentProps<PhosphorIcon>>
}) => {
  return (
    <Vertical className={"bg-primary/20 w-10 h-10 justify-center items-center"}>
      <Icon
        size={25}
        weight={"duotone"}
        duotoneColor={BFColors.midnightBlue}
        duotoneOpacity={0.2}
      />
    </Vertical>
  )
}

export const LoanProductCard = ({
  name,
  description,
  className,
  ltc,
  interest,
  origination,
  icon,
  noCta = false,
}: LoanProductCardProps) => {
  function checkEligibility() {
    preQualLandingPageIntentClick({
      intent: "Loan",
      id: `check-eligibility-${name}`,
      title: "Check Eligibility",
    })
    router.push("/pre-qual/properties-flipped")
  }

  return (
    <Container className={`gap-3 p-3 bg-grey ${className || ""}`}>
      <Horizontal>
        <Vertical className={"flex-1"}>
          <H4 color={"default"}>{name}</H4>
          <Tiny color={"default"}>{description}</Tiny>
        </Vertical>
        {icon}
      </Horizontal>
      <Vertical className={"gap-2"}>
        <Horizontal className={"justify-between items-center"}>
          <Small color={"default"}>LTC</Small>
          <H6 color={"default"}>{ltc}</H6>
        </Horizontal>
        <Horizontal className={"justify-between items-center"}>
          <Small color={"default"}>Interest</Small>
          <H6 color={"default"}>{interest}</H6>
        </Horizontal>
        <Horizontal className={"justify-between items-center"}>
          <Small color={"default"}>Origination</Small>
          <H6 color={"default"}>{origination}</H6>
        </Horizontal>
        {!noCta && (
          <BFButton
            onPress={checkEligibility}
            title={"Check Eligibility"}
            variant={"secondary"}
            size={"sm"}
            testID={`check-eligibility-${name}`}
          />
        )}
      </Vertical>
    </Container>
  )
}

interface ProductCardProps {
  withCta?: boolean
}

export const StandardProductCard = ({ withCta = false }: ProductCardProps) => {
  return (
    <LoanProductCard
      className={"w-[241px] min-h-[156px]"}
      name={"The Standard"}
      icon={<LoanProductIcon Icon={StarIcon} />}
      description={
        "Our core capital product, moderate leverage to boost total profit"
      }
      ltc={"85%"}
      interest={"from 7.50%"}
      origination={"from 1.50%"}
      noCta={!withCta}
    />
  )
}

export const AltitudeProductCard = ({ withCta = false }: ProductCardProps) => {
  return (
    <LoanProductCard
      className={"w-[241px] min-h-[156px]"}
      name={"Altitude"}
      icon={<LoanProductIcon Icon={MountainsIcon} />}
      description={"For elevated leverage with competitive pricing"}
      ltc={"92.5%"}
      interest={"from 9.25%"}
      origination={"from 1.75%"}
      noCta={!withCta}
    />
  )
}

export const ZeroGravityProductCard = ({
  withCta = false,
}: ProductCardProps) => {
  return (
    <LoanProductCard
      className={"w-[241px] min-h-[156px]"}
      name={"Zero Gravity"}
      icon={<LoanProductIcon Icon={RocketLaunchIcon} />}
      description={
        "No out of pocket lender fees, no interest payments for 6 months"
      }
      ltc={"90%"}
      interest={"from 10.00%"}
      origination={"2.00%"}
      noCta={!withCta}
    />
  )
}

export const DoubleDoubleProductCard = ({
  withCta = false,
}: ProductCardProps) => {
  return (
    <LoanProductCard
      className={"w-[241px] min-h-[156px]"}
      icon={<LoanProductIcon Icon={MoneyWavyIcon} />}
      name={"Double Double"}
      description={
        "Maximize your leverage to increase ROI on high-quality deals"
      }
      ltc={"93%"}
      interest={"from 12.75%"}
      origination={"2.00%"}
      noCta={!withCta}
    />
  )
}

export const ReFiProductCard = ({ withCta = false }: ProductCardProps) => {
  return (
    <LoanProductCard
      className={"w-[241px] min-h-[156px]"}
      name={"Refi + Rehab"}
      icon={<LoanProductIcon Icon={KeyIcon} />}
      description={
        "Refinancing option for rehab projects, aimed at maximizing ROI"
      }
      ltc={"85%"}
      interest={"from 11.00%"}
      origination={"from 1.50%"}
      noCta={!withCta}
    />
  )
}
